import { useState } from "react";
import axios from "axios";
import { Loader2, Lock, BarChart3 } from "lucide-react";
import { AdminUsers } from "../components/AdminUsers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, accent = "#10B981" }) => (
  <div className="card-38 p-4 hover:translate-y-0" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-muted-38 text-xs uppercase tracking-wide">{label}</div>
    <div className="font-display font-extrabold text-2xl mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const AdminStats = () => {
  const [key, setKey] = useState(localStorage.getItem("fc_admin_key") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": key } });
      setData(res.data);
      localStorage.setItem("fc_admin_key", key);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Wrong admin key." : "Couldn't load stats.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className="w-6 h-6 text-[#10B981]" />
          <h1 className="font-display font-extrabold text-2xl">Footie Clash — Admin Stats</h1>
        </div>

        <div className="card-38 p-4 flex flex-col sm:flex-row gap-3 hover:translate-y-0">
          <div className="flex items-center gap-2 flex-1 bg-white/5 rounded-xl px-3 border border-white/10">
            <Lock className="w-4 h-4 text-muted-38" />
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Admin key"
              className="flex-1 bg-transparent py-3 outline-none text-sm placeholder:text-muted-38"
            />
          </div>
          <button
            data-testid="admin-load-btn"
            onClick={load}
            disabled={loading || !key}
            className="btn-green rounded-xl px-6 py-3 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load stats"}
          </button>
        </div>

        {error && <p data-testid="admin-error" className="text-[#EF4444] text-sm mt-3">{error}</p>}

        {data && (
          <div className="mt-6 space-y-6" data-testid="admin-stats">
            <section>
              <h2 className="text-muted-38 text-sm font-semibold mb-2">Users</h2>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total users" value={data.users.total} />
                <Stat label="Registered" value={data.users.registered} accent="#38BDF8" />
                <Stat label="Guests" value={data.users.guests} accent="#94A3B8" />
              </div>
            </section>

            <section>
              <h2 className="text-muted-38 text-sm font-semibold mb-2">New signups</h2>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Last 24h" value={data.signups.last_24h} />
                <Stat label="Last 7 days" value={data.signups.last_7d} />
                <Stat label="Last 30 days" value={data.signups.last_30d} />
              </div>
            </section>

            <section>
              <h2 className="text-muted-38 text-sm font-semibold mb-2">Pep games</h2>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Games played" value={data.pep.games_played} />
                <Stat label="Wins" value={data.pep.wins} accent="#38BDF8" />
                <Stat label="Win rate" value={`${data.pep.win_rate_pct}%`} accent="#F59E0B" />
              </div>
            </section>

            <section>
              <h2 className="text-muted-38 text-sm font-semibold mb-2">Prize wheel</h2>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total spins" value={data.wheel.total_spins} />
                <Stat label="Prizes won" value={data.wheel.prizes_won} accent="#38BDF8" />
                <Stat label="Claims to fulfil" value={data.claims.pending_fulfilment} accent="#EF4444" />
              </div>
              {data.wheel.breakdown.length > 0 && (
                <div className="card-38 p-4 mt-3 hover:translate-y-0">
                  <div className="text-muted-38 text-xs uppercase tracking-wide mb-2">Prizes won by type</div>
                  <ul className="space-y-1.5 text-sm">
                    {data.wheel.breakdown.map((p) => (
                      <li key={p.label} className="flex justify-between">
                        <span className="text-slate-200">{p.label}</span>
                        <span className="font-semibold text-[#10B981] tabular-nums">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section>
              <h2 className="text-muted-38 text-sm font-semibold mb-2">Economy & multiplayer</h2>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Virtual £ in circulation" value={Number(data.coins_in_circulation).toLocaleString()} accent="#F59E0B" />
                <Stat label="Matches created" value={data.multiplayer.matches_created} accent="#38BDF8" />
                <Stat label="Total MP wins" value={data.multiplayer.total_wins} />
              </div>
            </section>

            <AdminUsers adminKey={key} />

            <button
              data-testid="admin-interest-csv-btn"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/admin/interest.csv`, { headers: { "X-Admin-Key": key }, responseType: "blob" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(res.data);
                  a.download = "footie-clash-early-access.csv";
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                } catch {
                  alert("Couldn't download the sign-ups, try again");
                }
              }}
              className="btn-green rounded-xl px-5 py-3 font-semibold"
            >
              Download early-access sign-ups (CSV)
            </button>

            <p className="text-muted-38 text-xs">Generated {new Date(data.generated_at).toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStats;
